import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TourOutlinedIcon from '@mui/icons-material/TourOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { memo, useCallback, useDeferredValue, useMemo, useState, type JSX, type ReactNode } from 'react';

import { HELP_PANEL_ATTR, locateElement } from './dom';
import { uiText, type HelpUiText } from './text';
import type { HelpLanguage, HelpStep, PageHelp } from './types';
import {
  groupStepsBySection,
  HELP_LANGUAGE_LABELS,
  HELP_LANGUAGES,
  localizedContext,
  localizedPage,
  localizedSection,
  localizedStep,
  roleBadgeLabel,
  stepMatchesQuery,
} from './types';

/**
 * The readable guide for the current screen, including the parts behind a dialog: the tour shows
 * only what is on screen, the drawer deliberately does not, so a field inside the New key dialog
 * is still readable without opening it.
 */

const DRAWER_WIDTH = 440;
/** How much of the panel stays on screen while it stands aside, so it never reads as closed. */
const YIELD_SLIVER = 14;
const HOVER_TINT = 'rgba(35, 85, 146, 0.06)';

const SectionLabel = ({ children }: { children: ReactNode }): JSX.Element => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 2.5, mb: 1 }}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: 'text.disabled',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
  </Stack>
);

const Pill = ({ label }: { label: string }): JSX.Element => (
  <Box
    component="span"
    sx={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: '#9A5A00',
      bgcolor: '#FFF1DE',
      borderRadius: 999,
      px: 0.75,
      py: 0.15,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const StepCardBody = ({
  step,
  language,
  text,
  contextHint,
  onLocate,
  onStartHere,
}: {
  step: HelpStep;
  language: HelpLanguage;
  text: HelpUiText;
  /** How to reveal the control, when the step lives behind a UI action. */
  contextHint?: string;
  /** Flashes the control and reports whether it was on screen. */
  onLocate: (step: HelpStep) => boolean;
  /** Opens the tour ON this step, so a card found by searching is one click from the walk. */
  onStartHere: (step: HelpStep) => void;
}): JSX.Element => {
  // Only set after a failed locate: the drawer does not probe the DOM up front, so a step is never
  // greyed out on a stale measurement taken before the user acted.
  const [missing, setMissing] = useState(false);
  const badge = roleBadgeLabel(step);
  // Falling back per field is the normal state of a part-translated guide; the pill says so
  // rather than mixing the languages silently.
  const { title, body, translated } = localizedStep(step, language);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        mb: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{title}</Typography>
            {badge && <Pill label={badge} />}
            {!translated && <Pill label={text.fallbackPill} />}
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5, lineHeight: 1.55 }}>
            {body}
          </Typography>
          {(missing || step.context) && contextHint && (
            <Typography
              sx={{
                fontSize: 11.5,
                color: missing ? 'warning.dark' : 'text.disabled',
                mt: 0.75,
                fontStyle: 'italic',
                lineHeight: 1.5,
              }}
            >
              {contextHint}
            </Typography>
          )}
          {missing && !contextHint && (
            <Typography sx={{ fontSize: 11.5, color: 'warning.dark', mt: 0.75, fontStyle: 'italic' }}>
              {text.notOnScreen}
            </Typography>
          )}
        </Box>
        <Stack sx={{ flexShrink: 0 }}>
          <Tooltip title={text.locateTooltip} placement="left">
            <IconButton
              size="small"
              aria-label={text.locateAria(title)}
              onClick={() => setMissing(!onLocate(step))}
              sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main', bgcolor: HOVER_TINT } }}
            >
              <MyLocationOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={text.startHereTooltip} placement="left">
            <IconButton
              size="small"
              aria-label={text.startHereAria(title)}
              onClick={() => onStartHere(step)}
              sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main', bgcolor: HOVER_TINT } }}
            >
              <PlayArrowOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * Memoised because of the search box: a card carries two `Tooltip`s, and re-rendering every card
 * per keystroke made typing stutter on the long guides (the keys editor has dozens of steps). The
 * props are stable per card, so a keystroke re-renders only the cards the filter added or removed.
 */
const StepCard = memo(StepCardBody);
StepCard.displayName = 'StepCard';

interface HelpDrawerProps {
  open: boolean;
  help: PageHelp;
  /** Role-filtered steps — the same list the launcher hands to the tour. */
  steps: HelpStep[];
  /** Whether the floating "?" is shown over the page (see help/preference.ts). */
  floating: boolean;
  onFloatingChange: (on: boolean) => void;
  /** The reader's language, owned by the launcher so all three surfaces agree on it. */
  language: HelpLanguage;
  onLanguageChange: (language: HelpLanguage) => void;
  onClose: () => void;
  /** Starts the tour — from the beginning, or at one step when the reader picked one. */
  onStartTour: (from?: HelpStep) => void;
  /** Hands over to click-to-explain: the reader points at the field instead of finding it here. */
  onInspect: () => void;
}

export const HelpDrawer = ({
  open,
  help,
  steps,
  floating,
  onFloatingChange,
  language,
  onLanguageChange,
  onClose,
  onStartTour,
  onInspect,
}: HelpDrawerProps): JSX.Element => {
  /**
   * Filters the entries on this page, over the BODIES too, for the reader who can describe the
   * field but not name it. The list follows the typing through a deferred value rather than a
   * debounce: there is no request to spare, only rendering, so the filter should be late rather
   * than delayed. Everything compared against the list reads the deferred query.
   */
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery],
  );
  const groups = useMemo(() => groupStepsBySection(help.sections, matches), [help.sections, matches]);
  /**
   * Set while the panel stands aside so a control underneath it can be seen. Not a close: the
   * scroll position and the query survive, and it slides back once the flash has faded.
   */
  const [yielding, setYielding] = useState(false);
  const page = localizedPage(help, language);
  const text = uiText(language);

  // Stable by contract: an identity that changed per render would defeat the memo on every card.
  const locate = useCallback(
    (step: HelpStep): boolean => locateElement(step, { onYield: setYielding }),
    [],
  );

  const sectionName = (section: string): string =>
    section === 'More' && !help.sections?.includes('More')
      ? text.moreSection
      : localizedSection(help, section, language);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the modal layer, matching the launcher: the guide has to be readable while the
      // dialog it documents is open.
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: DRAWER_WIDTH } },
          /**
           * Inline, not `sx`: MUI slides the paper with `<Slide>`, which writes `transform` onto
           * this node's style attribute — a class from `sx` would lose. A sliver stays on screen
           * so the panel never looks closed.
           */
          style: {
            transform: yielding ? `translateX(calc(100% - ${YIELD_SLIVER}px))` : undefined,
          },
          // Marks this as the guide's own panel, not the dialog a tour should stay inside.
          // Spread because MUI's slot-props type admits no data-* keys.
          ...({ [HELP_PANEL_ATTR]: 'drawer' } as Record<string, unknown>),
        },
        // Full contrast for the flash; `backgroundColor` rather than `opacity`, which MUI's Fade owns.
        backdrop: {
          sx: {
            backgroundColor: yielding ? 'transparent' : undefined,
            transition: 'background-color 180ms ease',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}
              >
                {text.eyebrow}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
                {page.title}
              </Typography>
            </Box>
            {/* The one place the language is chosen; the tour and the inspector follow it. */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={language}
              onChange={(_event, next: HelpLanguage | null) => {
                // An exclusive group reports null when the active button is pressed again.
                if (next) onLanguageChange(next);
              }}
              aria-label={text.languageAria}
              data-help="help-language"
              sx={{ '& .MuiToggleButton-root': { px: 0.9, py: 0.2, fontSize: 11, fontWeight: 700 } }}
            >
              {HELP_LANGUAGES.map((code) => (
                <ToggleButton key={code} value={code} lang={code}>
                  {HELP_LANGUAGE_LABELS[code]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton size="small" onClick={onClose} aria-label={text.close}>
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.5, lineHeight: 1.6 }}>
            {page.intro}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 1.75, flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<TourOutlinedIcon />}
              onClick={() => onStartTour()}
              data-help="help-tour-button"
            >
              {text.showMeAround}
            </Button>
            {/* The way out of a long walk: the reader who wants ONE field points at it instead of
                stepping through everything in front of it. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<TouchAppOutlinedIcon />}
              onClick={onInspect}
              data-help="help-inspect-button"
            >
              {text.pointAtField}
            </Button>
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.75 }}>
            {text.drawerHint}
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text.searchPlaceholder(steps.length)}
            data-help="help-page-search"
            sx={{ mt: 1.5 }}
            slotProps={{
              htmlInput: { maxLength: 60, 'aria-label': text.searchAria },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, pb: 3 }}>
          {deferredQuery.length > 0 && (
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1.5 }}>
              {matches.length === 0
                ? text.nothingMatches
                : text.matchLine(matches.length, steps.length, deferredQuery)}
            </Typography>
          )}

          {groups.map((group) => (
            <Box key={group.section}>
              <SectionLabel>{sectionName(group.section)}</SectionLabel>
              {group.steps.map((step) => (
                <StepCard
                  key={step.selector}
                  step={step}
                  language={language}
                  text={text}
                  contextHint={step.context ? localizedContext(help, step.context, language) : undefined}
                  onLocate={locate}
                  onStartHere={onStartTour}
                />
              ))}
            </Box>
          ))}

          {/* Hidden while filtering: the tips answer no search, and leaving them under an empty
              result reads as a match. */}
          {deferredQuery.length === 0 && page.tips && page.tips.length > 0 && (
            <>
              <SectionLabel>{text.goodToKnow}</SectionLabel>
              <Stack spacing={1.25}>
                {page.tips.map((tip) => (
                  <Stack key={tip} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TipsAndUpdatesOutlinedIcon
                      sx={{ fontSize: 16, color: 'primary.main', mt: 0.25, flexShrink: 0 }}
                    />
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.55 }}>
                      {tip}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Box>

        {/* Where the floating "?" is turned back on — off by default because the bottom-right
            corner belongs to the tables' pagination and row actions. */}
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.default',
          }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={floating}
                onChange={(event) => onFloatingChange(event.target.checked)}
                slotProps={{ input: { 'aria-label': text.floatingSwitchAria } }}
              />
            }
            label={<Typography sx={{ fontSize: 12.5, fontWeight: 600, ml: 1 }}>{text.floatingLabel}</Typography>}
          />
          <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
            {floating ? text.floatingOn : text.floatingOff}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};
